/**
 * WhoItServes.jsx — Subtab III
 *
 * Stacked accordion of seven audience cards. First card open by default;
 * tapping a header expands its body and collapses the rest.
 */

import React, { useState } from "react";
import { cxTokens as t } from "../../../lib/coconsolexTokens.js";
import { SubtabHeading, Body, P } from "../SubtabFrame.jsx";

const SCALES_ICON = (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#3D5A80" strokeWidth="1.6" aria-hidden>
    <line x1="16" y1="4" x2="16" y2="28" />
    <line x1="6" y1="10" x2="26" y2="10" />
    <path d="M 6 10 L 2 18 L 10 18 Z" />
    <path d="M 26 10 L 22 18 L 30 18 Z" />
  </svg>
);
const CAPITOL_ICON = (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#3D5A80" strokeWidth="1.6" aria-hidden>
    <rect x="4" y="20" width="24" height="8" />
    <path d="M 8 20 L 8 14 M 12 20 L 12 14 M 16 20 L 16 14 M 20 20 L 20 14 M 24 20 L 24 14" />
    <path d="M 6 14 L 26 14" />
    <path d="M 16 4 Q 6 8 8 14 M 16 4 Q 26 8 24 14" />
  </svg>
);
const MAGNIFIER_ICON = (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#3D5A80" strokeWidth="1.6" aria-hidden>
    <rect x="4" y="6" width="18" height="22" />
    <line x1="8" y1="12" x2="18" y2="12" />
    <line x1="8" y1="16" x2="18" y2="16" />
    <circle cx="22" cy="22" r="6" />
    <line x1="26" y1="26" x2="30" y2="30" />
  </svg>
);
const LINKED_ICON = (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#3D5A80" strokeWidth="1.6" aria-hidden>
    <circle cx="10" cy="10" r="4" />
    <circle cx="22" cy="10" r="4" />
    <path d="M 6 28 Q 10 18 14 28 M 18 28 Q 22 18 26 28" />
    <line x1="14" y1="10" x2="18" y2="10" />
  </svg>
);
const BOOK_ICON = (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#3D5A80" strokeWidth="1.6" aria-hidden>
    <path d="M 4 6 L 16 10 L 28 6 L 28 26 L 16 30 L 4 26 Z" />
    <line x1="16" y1="10" x2="16" y2="30" />
    <line x1="20" y1="2" x2="20" y2="14" />
  </svg>
);
const GAVEL_ICON = (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#3D5A80" strokeWidth="1.6" aria-hidden>
    <rect x="14" y="4" width="14" height="8" transform="rotate(45 21 8)" />
    <line x1="6" y1="22" x2="14" y2="14" />
    <line x1="2" y1="28" x2="14" y2="28" />
  </svg>
);
const FIGURE_ICON = (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="#3D5A80" strokeWidth="1.6" aria-hidden>
    <circle cx="12" cy="8" r="4" />
    <path d="M 6 28 L 6 16 Q 12 12 18 16 L 18 28" />
    <rect x="20" y="14" width="10" height="14" />
    <line x1="22" y1="18" x2="28" y2="18" />
    <line x1="22" y1="22" x2="28" y2="22" />
  </svg>
);

const AUDIENCES = [
  {
    title: "Private Sector Law Firms",
    icon: SCALES_ICON,
    body:
      "AmLaw 100 firms are running parallel pilots of Harvey, CoCounsel, and Lexis+ AI while their general counsels and risk committees are increasingly uncomfortable with the structural confidentiality posture. Firms representing M&A clients, white-collar defendants, defense contractors, healthcare companies, financial institutions, and qui tam plaintiffs are facing client-side audit demands that the cloud AI vendors cannot meaningfully satisfy. Mid-market firms and boutiques in regulated practice areas face the same pressure with thinner margins for error. The demand signal is loud — firm RFPs increasingly ask about AI governance, data residency, and exit provisions — and the existing market is meeting it with contractual reassurance rather than structural answer.",
  },
  {
    title: "Federal Government Agencies (Non-Classified)",
    icon: CAPITOL_ICON,
    body:
      "Federal agencies conducting legal work — the Department of Justice's litigating divisions, the Office of General Counsel functions across cabinet departments, regulatory enforcement components at SEC, FTC, CFPB, and HHS-OIG — face acute AI confidentiality problems. Agency attorneys handling enforcement matters, regulatory adjudications, FOIA litigation, and personnel actions cannot transmit privileged deliberative material or pre-decisional analysis to cloud AI vendors without raising deliberative process privilege and law enforcement privilege concerns. FedRAMP High authorization helps with some categories but does not address the underlying privilege question. Agency General Counsel offices are actively looking for deployment models that solve the structural problem.",
  },
  {
    title: "Federal Inspectors General and OGC",
    icon: MAGNIFIER_ICON,
    body:
      "This is perhaps the sharpest demand signal in the federal space. IG investigations involve pre-decisional investigative material, confidential whistleblower communications, ongoing law enforcement information, and sensitive personnel matters. OGC work involves the agency's own privileged deliberations. Both functions are increasingly tasked with handling AI-related investigations themselves while being unable to safely use AI tools for their own analytical work. The current state — where IG analysts cannot use AI to triage thousands of complaints because doing so would expose protected information — is operationally untenable as caseloads grow.",
  },
  {
    title: "Federal Employee Unions and Labor Organizations",
    icon: LINKED_ICON,
    body:
      "Labor representation involves confidential bargaining unit member communications, grievance materials covered by statutory protections, unfair labor practice charge investigations, and arbitration preparation that often touches whistleblower-protected information. AFGE, NTEU, NAGE, and the federal sector unions face the same AI confidentiality problem private firms face, with smaller budgets and higher political sensitivity. Local and council-level union legal staff need AI capability for research and drafting but cannot use cloud tools for member-protected information without creating duty-of-fair-representation exposure.",
  },
  {
    title: "Universities and Law Schools",
    icon: BOOK_ICON,
    body:
      "University General Counsel offices handle Title IX matters, employment investigations, research integrity proceedings, and student conduct adjudications — all categories of work where confidentiality is statutory rather than merely customary. FERPA, Title IX, and institutional research data protections create structural barriers to cloud AI use that universities are currently navigating with ad hoc internal policies that don't scale. Law schools face a separate but related problem: teaching legal AI literacy to students who will practice in a profession that increasingly requires it, while not exposing student work product or clinical client information to cloud AI vendors. Clinical programs handling actual client matters face the full Rule 1.6 problem.",
  },
  {
    title: "State and Federal Judges and Judicial Staff",
    icon: GAVEL_ICON,
    body:
      "This segment is the most underserved and arguably the most consequential. Judges and law clerks at every level — from local municipal courts through federal district courts, courts of appeal, state supreme courts, and the United States Supreme Court — face AI confidentiality problems with no current commercial answer. Chambers handle pre-decisional drafts, internal deliberations among judges, sealed materials, in camera submissions, and confidential settlement communications. The federal judiciary's PACER and CM/ECF systems are not authorized for any cloud AI integration. State judicial branches have varying and largely undeveloped policies. Law clerks who could materially benefit from AI-assisted research and drafting are operating without it because the existing tools are not appropriate for chambers work. The demand here is large, latent, and currently unaddressed.",
  },
  {
    title: "Self-Represented Individuals",
    icon: FIGURE_ICON,
    body:
      "Pro se litigants — particularly in family law, landlord-tenant, immigration, veterans benefits, Social Security disability, and small claims — face an access-to-justice problem that AI could meaningfully address, except that the same individuals often cannot understand or navigate the data-handling implications of cloud AI tools. Their cases involve confidential personal information, medical records, immigration status, financial distress, and family safety information that they should not be transmitting to commercial AI vendors but frequently are because the alternative is no legal assistance at all. Legal aid organizations attempting to serve this population face the institutional version of the same problem — they cannot deploy AI to scale their services without exposing client information that is statutorily and ethically protected.",
  },
];

export default function WhoItServes() {
  const [open, setOpen] = useState(0);

  return (
    <article>
      <SubtabHeading roman="III">The Current Demand Signal</SubtabHeading>
      <p
        style={{
          fontFamily: "'Inter', sans-serif",
          fontSize: 17,
          lineHeight: 1.7,
          color: t.slate,
          marginTop: 0,
          marginBottom: 36,
        }}
      >
        Across every segment of the legal profession, the lawyers who need AI
        most are precisely those who cannot use cloud-based tools without
        exposing privileged, sensitive, or protected information.
      </p>

      <div>
        {AUDIENCES.map((a, i) => {
          const isOpen = open === i;
          return (
            <div
              key={i}
              style={{
                borderTop: `1px solid ${t.rule}`,
                borderBottom: i === AUDIENCES.length - 1 ? `1px solid ${t.rule}` : "none",
              }}
            >
              <button
                onClick={() => setOpen(isOpen ? -1 : i)}
                aria-expanded={isOpen}
                style={{
                  width: "100%",
                  textAlign: "left",
                  background: "none",
                  border: "none",
                  padding: "20px 0",
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  cursor: "pointer",
                  minHeight: 64,
                }}
              >
                <span style={{ flexShrink: 0 }}>{a.icon}</span>
                <span
                  style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontSize: 22,
                    color: t.ink,
                    fontWeight: 500,
                    flex: 1,
                  }}
                >
                  {a.title}
                </span>
                <span
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 18,
                    color: t.slate,
                    transition: "transform 200ms",
                    transform: isOpen ? "rotate(45deg)" : "rotate(0deg)",
                  }}
                >
                  +
                </span>
              </button>
              <div
                aria-hidden={!isOpen}
                style={{
                  maxHeight: isOpen ? 1200 : 0,
                  overflow: "hidden",
                  transition: "max-height 320ms ease",
                }}
              >
                <p
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 15.5,
                    lineHeight: 1.75,
                    color: t.ink,
                    margin: "0 0 22px 48px",
                    paddingRight: 12,
                  }}
                >
                  {a.body}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Common thread closing card */}
      <div
        style={{
          marginTop: 40,
          background: t.ink,
          color: t.cream,
          padding: "32px 28px",
        }}
      >
        <div
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontStyle: "italic",
            color: "#A3BFFA",
            fontSize: 14,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            marginBottom: 12,
          }}
        >
          The common thread
        </div>
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 16, lineHeight: 1.7, margin: "0 0 14px" }}>
          Across all of these segments, the structural problem is identical:
          the legal work that AI can most usefully assist is, by its nature,
          confidential, privileged, or protected. The current market offers
          cloud-based tools that handle the AI part well and the
          confidentiality part through contractual assurance. The gap is not
          capability. The gap is structural protection of the information being
          processed.
        </p>
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 16, lineHeight: 1.7, margin: 0 }}>
          The current demand signal, taken across all segments, is the signal
          of a market that knows it needs AI but does not have an architecture
          it can ethically and legally deploy at scale. CoConsoleX is that
          architecture.
        </p>
      </div>
    </article>
  );
}
